import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import '../Services/Services.css';
import './LongTermRental.css';

const DURATION_OPTIONS = [
  { id: 1, label: '1 Month', months: 1 },
  { id: 6, label: '6 Months', months: 6 },
];

const LongTermRental = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(1);
  const [countrySearch, setCountrySearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [renting, setRenting] = useState(false);
  const [error, setError] = useState('');
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await api.get('/v1/users/long-term-rentals/options');
        if (response?.data) {
          const list = Array.isArray(response.data) ? response.data : response.data.countries || [];
          setCountries(list);
        }
      } catch (err) {
        console.error('Failed to fetch long-term countries:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    if (!isAuthenticated()) return;
    const fetchBalance = async () => {
      try {
        const response = await api.get('/v1/users', true);
        if (response?.data?.user?.userBalance) {
          setBalance(response.data.user.userBalance);
        }
      } catch (err) {
        console.error('Failed to fetch balance:', err);
      }
    };
    fetchBalance();
  }, [isAuthenticated]);

  const filteredCountries = useMemo(() => {
    if (!countrySearch.trim()) return countries;
    return countries.filter(c =>
      c.countryName.toLowerCase().includes(countrySearch.toLowerCase())
    );
  }, [countries, countrySearch]);

  const getPriceForDuration = (country, months) => {
    if (!country?.prices) return null;
    const priceOption = country.prices.find(p => p.months === months);
    return priceOption?.coins ?? null;
  };

  const getSelectedPrice = (country) => {
    return getPriceForDuration(country, selectedDuration);
  };

  const handleRent = async () => {
    if (!selectedCountry || renting) return;

    if (!isAuthenticated()) {
      navigate('/auth');
      return;
    }

    setRenting(true);
    setError('');

    try {
      const response = await api.post(
        '/v1/users/long-term-rentals/rent',
        {
          countryCode: selectedCountry.countryCode,
          phoneCode: selectedCountry.phoneCode,
          rentalDurationMonths: selectedDuration,
          provider: selectedCountry.provider,
        },
        true
      );

      if (response?.data) {
        navigate('/verification', {
          state: {
            rental: response,
            countryName: selectedCountry.countryName,
          },
        });
      }
    } catch (err) {
      setError(err.data?.message || err.message || 'Failed to rent number. Please try again.');
    } finally {
      setRenting(false);
    }
  };

  return (
    <div className="longterm-page">
      <div className="longterm-container">
        {/* Header */}
        <div className="longterm-header">
          <div className="longterm-header-text">
            <h1 className="longterm-title">Long Term Number Rental</h1>
            <p className="longterm-subtitle">
              Rent a dedicated phone number for 1 month or 6 months
            </p>
          </div>
          {isAuthenticated() && balance && (
            <div className="longterm-balance">
              <span className="material-symbols-outlined">toll</span>
              <span className="longterm-balance-amount">{balance.coins?.toLocaleString() ?? 0}</span>
              <span className="longterm-balance-label">coins</span>
            </div>
          )}
        </div>

        <div className="longterm-grid">
          {/* Left - Country Selection */}
          <div className="longterm-main">
            {/* Duration Selection */}
            <div className="longterm-card">
              <div className="longterm-card-header">
                <span className="step-number">1</span>
                <h2 className="longterm-card-title">Select Duration</h2>
              </div>
              <div className="duration-options">
                {DURATION_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    className={`duration-btn ${selectedDuration === option.id ? 'active' : ''}`}
                    onClick={() => setSelectedDuration(option.id)}
                  >
                    <span className="material-symbols-outlined duration-icon">calendar_month</span>
                    <span className="duration-label">{option.label}</span>
                    {option.id === 6 && (
                      <span className="duration-badge">Best Value</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Country Selection */}
            <div className="longterm-card">
              <div className="longterm-card-header">
                <span className="step-number">2</span>
                <h2 className="longterm-card-title">Select Country</h2>
              </div>

              <div className="longterm-search-wrapper">
                <span className="material-symbols-outlined longterm-search-icon">search</span>
                <input
                  type="text"
                  className="longterm-search-input"
                  placeholder="Search country..."
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                />
              </div>

              <div className="longterm-countries-list">
                {loading ? (
                  <div className="longterm-loading">
                    <div className="longterm-spinner"></div>
                    <p>Loading countries...</p>
                  </div>
                ) : filteredCountries.length === 0 ? (
                  <div className="longterm-empty">
                    <span className="material-symbols-outlined">public_off</span>
                    <p>No countries available</p>
                  </div>
                ) : (
                  filteredCountries.map((country) => {
                    const price = getSelectedPrice(country);
                    return (
                      <div
                        key={country._id || `${country.countryCode}-${country.provider}`}
                        className={`longterm-country-item ${selectedCountry?.countryCode === country.countryCode && selectedCountry?.provider === country.provider ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedCountry(country);
                          setError('');
                        }}
                      >
                        <div className="longterm-country-info">
                          {country.countryFlagUrl && (
                            <img src={country.countryFlagUrl} alt={country.countryName} className="longterm-country-logo" />
                          )}
                          <span className="longterm-country-name">
                            {country.countryName}
                          </span>
                        </div>
                        <div className="longterm-country-price-section">
                          <span className="longterm-country-price">
                            {price !== null ? `${price.toLocaleString()} coins` : '—'}
                          </span>
                          {selectedCountry?.countryCode === country.countryCode && selectedCountry?.provider === country.provider ? (
                            <div className="longterm-check-icon">
                              <span className="material-symbols-outlined">check</span>
                            </div>
                          ) : (
                            <span className="material-symbols-outlined longterm-chevron">chevron_right</span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Right - Summary */}
          <aside className="longterm-sidebar">
            <div className="longterm-card summary-card">
              <h3 className="summary-title">Rental Summary</h3>

              <div className="summary-divider"></div>

              <div className="summary-details">
                <div className="summary-row">
                  <span className="summary-label">Duration</span>
                  <span className="summary-value">
                    {DURATION_OPTIONS.find(d => d.id === selectedDuration)?.label}
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Country</span>
                  <span className="summary-value">
                    {selectedCountry ? (
                      <>
                        {selectedCountry.countryFlagUrl && (
                          <img src={selectedCountry.countryFlagUrl} alt={selectedCountry.countryName} className="summary-country-logo" />
                        )}
                        {selectedCountry.countryName}
                      </>
                    ) : 'Not selected'}
                  </span>
                </div>
                {selectedCountry && (
                  <>
                    {selectedCountry.prices && selectedCountry.prices.length > 1 && (
                      <div className="summary-prices">
                        {selectedCountry.prices.map((p) => (
                          <div
                            key={p.months}
                            className={`summary-price-option ${p.months === selectedDuration ? 'active' : ''}`}
                          >
                            <span>{p.months} {p.months === 1 ? 'month' : 'months'}</span>
                            <span>{p.coins.toLocaleString()} coins</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="summary-divider"></div>

              <div className="summary-total-row">
                <span className="summary-total-label">Total</span>
                <span className="summary-total-value">
                  {selectedCountry
                    ? `${(getSelectedPrice(selectedCountry) ?? 0).toLocaleString()} coins`
                    : '—'}
                </span>
              </div>

              {error && (
                <div className="summary-error">
                  <span className="material-symbols-outlined">error</span>
                  <span>{error}</span>
                </div>
              )}

              <button
                className="rent-btn"
                onClick={handleRent}
                disabled={renting || !selectedCountry || getSelectedPrice(selectedCountry) === null}
              >
                {renting ? (
                  <>
                    <span className="btn-spinner"></span>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined">phone_android</span>
                    <span>Rent Number</span>
                  </>
                )}
              </button>

              <p className="summary-note">
                Your number will be active for the entire rental period. You can receive unlimited SMS during this time.
              </p>

              <div className="summary-trust">
                <div className="trust-item">
                  <span className="material-symbols-outlined">lock</span>
                  <span>Dedicated</span>
                </div>
                <div className="trust-item">
                  <span className="material-symbols-outlined">all_inbox</span>
                  <span>Unlimited SMS</span>
                </div>
                <div className="trust-item">
                  <span className="material-symbols-outlined">autorenew</span>
                  <span>Renewable</span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default LongTermRental;
